import React, { useImperativeHandle, forwardRef, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { Button, Flex } from "@chakra-ui/react";
import { EmailIcon, ViewIcon, EditIcon } from "@chakra-ui/icons";
import "./emailEditor.css";

const EmailEditor = forwardRef(({ name, control, defaultValue = "", onFocus, onSendTest }, ref) => {
  const textareaRef = useRef(null);
  const savedSelectionRef = useRef({ start: 0, end: 0 });
  const [showPreview, setShowPreview] = useState(false);

  useImperativeHandle(ref, () => ({
    insertText: (text) => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      const { start, end } = savedSelectionRef.current;
      const value = textarea.value;
      const newValue = value.slice(0, start) + text + value.slice(end);
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLTextAreaElement.prototype,
        "value"
      ).set;
      const scrollTop = textarea.scrollTop;
      nativeInputValueSetter.call(textarea, newValue);
      textarea.dispatchEvent(new Event("input", { bubbles: true }));
      const cursorPos = start + text.length;
      savedSelectionRef.current = { start: cursorPos, end: cursorPos };
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(cursorPos, cursorPos);
        textarea.scrollTop = scrollTop;
      }, 0);
    },
  }));

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field }) => (
        <div className="email-editor">
          <Flex justify="flex-end" gap={2} mb="6px">
            <Button
              type="button"
              size="sm"
              colorScheme="blue"
              variant={showPreview ? "solid" : "outline"}
              leftIcon={showPreview ? <EditIcon /> : <ViewIcon />}
              onClick={() => setShowPreview((v) => !v)}
            >
              {showPreview ? "Edit" : "Preview"}
            </Button>
            {onSendTest && (
              <Button
                type="button"
                size="sm"
                colorScheme="blue"
                variant="outline"
                leftIcon={<EmailIcon />}
                onClick={onSendTest}
              >
                Send Test
              </Button>
            )}
          </Flex>
          {showPreview ? (
            <iframe
              className="email-editor__preview-iframe"
              srcDoc={field.value || ""}
              title="Email Preview"
              sandbox="allow-same-origin"
            />
          ) : (
            <textarea
              className="email-editor__textarea"
              value={field.value || ""}
              onChange={(e) => field.onChange(e.target.value)}
              onFocus={() => { if (onFocus) onFocus(); }}
              onSelect={(e) => {
                savedSelectionRef.current = {
                  start: e.target.selectionStart,
                  end: e.target.selectionEnd,
                };
              }}
              onBlur={(e) => {
                savedSelectionRef.current = {
                  start: e.target.selectionStart,
                  end: e.target.selectionEnd,
                };
              }}
              spellCheck={false}
              ref={(el) => {
                field.ref(el);
                textareaRef.current = el;
              }}
            />
          )}
        </div>
      )}
    />
  );
});

export default EmailEditor;
